import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { getCategoryList } from "src/api/category";
import { Subjects } from "src/api/product";
import { CATEGORY_QUERY_KEYS } from "./useCategoryMenu";
/**
 * 상품 타입별 freeTrial 객체 생성
 */ const getMockFreeTrial = (type)=>{
    switch(type){
        case "basic":
            return undefined;
        case "freeTrial":
            return {
                eligible: true,
                reason: null,
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "freeTrialNotLoggedIn":
            return {
                eligible: null,
                reason: "not_logged_in",
                remainingToday: null,
                promotionId: 1,
                dailyLimit: 5
            };
        case "trialOver":
            return {
                eligible: false,
                reason: "daily_limit_exceeded",
                remainingToday: 0,
                promotionId: 1,
                dailyLimit: 5
            };
        case "owned":
            return {
                eligible: false,
                reason: "already_owned",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "excluded":
            return {
                eligible: false,
                reason: "excluded_membership",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        case "notTargetUser":
            return {
                eligible: false,
                reason: "not_target_user",
                remainingToday: 3,
                promotionId: 1,
                dailyLimit: 5
            };
        default:
            return undefined;
    }
};
/**
 * 상품 데이터에 mock 타입 적용
 */ const applyMockToProducts = (products, mockType, mockLicensePlan)=>{
    if (!mockType && !mockLicensePlan) return products;
    return products.map((product)=>({
            ...product,
            ...mockType && {
                freeTrial: getMockFreeTrial(mockType)
            },
            ...mockLicensePlan && {
                licensePlan: mockLicensePlan === "null" ? null : mockLicensePlan
            }
        }));
};
/**
 * 카테고리별 상품 리스트를 조회하는 훅
 * 엔드포인트: GET /menu-categories/:categoryId
 */ export const useCategoryProducts = (param)=>{
    let { categoryId , offset =0 , limit =24 , sort , filters  } = param;
    var _query_data, _query_data1, _query_data_sortItems, _query_data2, _query_data_filterItems, _query_data3;
    const router = useRouter();
    // Mock 쿼리파라미터 읽기
    const mockProductType = router.query.mockProductType;
    const mockLicensePlan = router.query.mockLicensePlan;
    const isEnabled = !!categoryId && typeof categoryId === "string";
    const query = useQuery({
        queryKey: [
            CATEGORY_QUERY_KEYS.CATEGORY_PRODUCTS,
            categoryId,
            offset,
            limit,
            sort,
            filters
        ],
        queryFn: ()=>{
            var _filters_units, _filters_dataTypes, _filters_authors, _filters_solvookPasses, _filters_steps;
            return getCategoryList({
                id: categoryId,
                offset,
                limit,
                ...sort && {
                    sort: sort
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_units = filters.units) === null || _filters_units === void 0 ? void 0 : _filters_units.length) && {
                    "units[]": filters.units
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_dataTypes = filters.dataTypes) === null || _filters_dataTypes === void 0 ? void 0 : _filters_dataTypes.length) && {
                    "dataTypes[]": filters.dataTypes
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_authors = filters.authors) === null || _filters_authors === void 0 ? void 0 : _filters_authors.length) && {
                    "authors[]": filters.authors
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_solvookPasses = filters.solvookPasses) === null || _filters_solvookPasses === void 0 ? void 0 : _filters_solvookPasses.length) && {
                    "solvookPasses[]": filters.solvookPasses
                },
                ...(filters === null || filters === void 0 ? void 0 : (_filters_steps = filters.steps) === null || _filters_steps === void 0 ? void 0 : _filters_steps.length) && {
                    "steps[]": filters.steps
                }
            });
        },
        enabled: isEnabled,
        refetchOnWindowFocus: false,
        keepPreviousData: true
    });
    var _item_badgeText, _item_rating, _item_isCouponAvailable, _item_unit, _item_category_tag, _item_category_plus, _item_brandName, _item_subject, _item_licensePlan;
    // 변환된 상품 리스트 (EditionCategoryProduct 타입에 맞춤)
    const rawProducts = ((_query_data = query.data) === null || _query_data === void 0 ? void 0 : _query_data.items.map((item)=>{
        var _item_category, _item_category1;
        return {
            id: String(item.id),
            sourceId: String(item.sourceId),
            licenseId: item.licenseId,
            title: item.title,
            imageUrl: item.imageUrl,
            badgeText: (_item_badgeText = item.badgeText) !== null && _item_badgeText !== void 0 ? _item_badgeText : null,
            author: item.author,
            authorId: item.authorId ? String(item.authorId) : undefined,
            // freepass는 price가 문자열("쏠북패스 이용시 0원")로 오므로 그대로 사용
            price: item.handoutType === "freepass" ? item.price : typeof item.price === "string" ? parseInt(item.price, 10) || 0 : item.price,
            rating: (_item_rating = item.rating) !== null && _item_rating !== void 0 ? _item_rating : null,
            isCouponAvailable: (_item_isCouponAvailable = item.isCouponAvailable) !== null && _item_isCouponAvailable !== void 0 ? _item_isCouponAvailable : false,
            unit: (_item_unit = item.unit) !== null && _item_unit !== void 0 ? _item_unit : "",
            category: {
                tag: (_item_category_tag = (_item_category = item.category) === null || _item_category === void 0 ? void 0 : _item_category.tag) !== null && _item_category_tag !== void 0 ? _item_category_tag : "",
                plus: (_item_category_plus = (_item_category1 = item.category) === null || _item_category1 === void 0 ? void 0 : _item_category1.plus) !== null && _item_category_plus !== void 0 ? _item_category_plus : 0
            },
            brandName: (_item_brandName = item.brandName) !== null && _item_brandName !== void 0 ? _item_brandName : "",
            subject: (_item_subject = item.subject) !== null && _item_subject !== void 0 ? _item_subject : Subjects.KO,
            freeTrial: item.freeTrial,
            authorBrandUrl: item.brandName ? "/@".concat(item.brandName) : undefined,
            reviewCount: undefined,
            purchaseOption: undefined,
            // 라이센스 플랜 (뷰어/다운로드 버튼 표시용)
            licensePlan: (_item_licensePlan = item.licensePlan) !== null && _item_licensePlan !== void 0 ? _item_licensePlan : null,
            // 이용 수단(이용권/구독) 사용 관련 필드
            usableMembershipId: item.usableMembershipId,
            usableSubscriptionId: item.usableSubscriptionId,
            recommendedEntitlement: item.recommendedEntitlement,
            purchaseOptions: item.purchaseOptions,
            // freepass 자료 구분용
            handoutType: item.handoutType,
            // N-PDF 관련 필드
            hasMultiplePdfs: item.hasMultiplePdfs,
            pdfCount: item.pdfCount
        };
    })) || [];
    // Mock 적용
    const products = applyMockToProducts(rawProducts, mockProductType, mockLicensePlan);
    // 정렬 옵션
    const sortOptions = ((_query_data1 = query.data) === null || _query_data1 === void 0 ? void 0 : (_query_data_sortItems = _query_data1.sortItems) === null || _query_data_sortItems === void 0 ? void 0 : _query_data_sortItems.map((label)=>({
            value: label,
            label
        }))) || [];
    // 필터 항목 (ProductFilter용)
    const filterItems = ((_query_data2 = query.data) === null || _query_data2 === void 0 ? void 0 : (_query_data_filterItems = _query_data2.filterItems) === null || _query_data_filterItems === void 0 ? void 0 : _query_data_filterItems.map((filter)=>({
            name: filter.name,
            key: filter.key,
            items: filter.items
        }))) || [];
    return {
        ...query,
        products,
        totalCount: ((_query_data3 = query.data) === null || _query_data3 === void 0 ? void 0 : _query_data3.totalCount) || 0,
        sortOptions,
        filterItems,
        /** 필터 로딩 상태 - 최초 로딩 시에만 true */ isFilterLoading: query.isLoading && !query.data
    };
};
